import { useEffect, useId, useRef, useState } from 'react';
import { IconInfoSolid } from './icons';
import { LifecycleStepBar } from './LifecycleStepBar';

/**
 * Page-level (i) control for packaging/custody lifecycle.
 * Closed by default so the stepper is not client-facing chrome.
 * Opens in-flow under the header (not a modal) so primary CTAs stay clickable.
 */
export function LifecycleInfoPopover({
  steps,
  title = 'Packaging lifecycle',
  children,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelId = useId();
  const hasSteps = Boolean(steps?.length);

  useEffect(() => {
    if (!open) return undefined;

    function onKey(event) {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    }

    function onDocumentClick(event) {
      if (rootRef.current?.contains(event.target)) return;
      setOpen(false);
    }

    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onDocumentClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDocumentClick);
    };
  }, [open]);

  const trigger = hasSteps ? (
    <button
      ref={buttonRef}
      type="button"
      aria-label={title}
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      onClick={() => setOpen((value) => !value)}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] border border-[#F1F1F1] bg-[#F8F8F8] text-[#003A70] transition-colors hover:bg-[#EFEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/25"
    >
      <IconInfoSolid className="h-3.5 w-3.5" />
    </button>
  ) : null;

  return (
    <div ref={rootRef}>
      {typeof children === 'function' ? children(trigger) : children}
      {open && hasSteps ? (
        <div id={panelId} role="region" className="mt-4">
          <LifecycleStepBar steps={steps} title={title} />
        </div>
      ) : null}
    </div>
  );
}
